import { z } from 'zod';
/** `stored` is a drop that landed and was deliberately not analysed: saved, waiting for the Analyse button. */
export const AssetStatus = z.enum(['awaiting_upload', 'stored', 'awaiting_snippet', 'confirming', 'indexed', 'hidden', 'deleted']);
export const SnippetRole = z.enum(['hook', 'reveal', 'process', 'texture', 'face', 'product', 'broll', 'other']);
/** Seed vocabulary for a brand's managed role list; "other" is deliberately absent. */
export const DEFAULT_SNIPPET_ROLES = ['hook', 'reveal', 'process', 'texture', 'face', 'product', 'broll'];
/** A brand-managed role label: free text once the brand edits its list. */
export const RoleLabel = z.string().min(1).max(40);
/**
 * A highlight is the AI's pick of a moment; an extra is a frame-tight stand-out (a steady colour, a
 * still pack shot) for the Video extras tab; a scene is one span of the whole clip, cut where the
 * picture changes. All three are index rows that search and the composer can call up.
 */
export const SnippetKind = z.enum(['highlight', 'extra', 'scene']);
/** What a row is called where it has no heading and no classifier of its own. */
export const SNIPPET_KIND_LABELS = { highlight: 'Highlight', extra: 'Extra', scene: 'Scene' };
/** What an audio snippet of each kind is called where it has no heading of its own. */
export const PHRASE_KIND_LABELS = { voice: 'Spoken voice', effect: 'Sound effect', ambient: 'Ambience', music: 'Music', mute: 'Silence' };
/** Who drew a row: the analysis, or a person. A re-analysis replaces the first and keeps the second. */
export const SnippetSource = z.enum(['ai', 'manual']);
/**
 * What an audio snippet is: a spoken line, or a sound the analysis heard and described (ambience, an
 * effect, music, silence). A person can reclassify any of them (Dax, 2026-09-25).
 */
export const PhraseKind = z.enum(['voice', 'ambient', 'effect', 'music', 'mute']);
/**
 * The kinds that can be saved to the audio library, and what each becomes there. A spoken line is a
 * voiceover (Dax, 2026-09-29); silence is nothing to reuse.
 */
export const SAVED_SOUND_KIND = { effect: 'sfx', ambient: 'bed', music: 'bed', voice: 'voice' };
/**
 * Where an audio snippet stands: described (indexed and searchable only), saved to the brand's audio
 * library, or shared from the house library with every tenant.
 */
export const SoundStatus = z.enum(['described', 'library', 'global']);
export const RegionKind = z.enum(['speech', 'noise', 'quiet']);
export const TagSource = z.enum(['detected', 'suggested', 'frequent', 'user', 'inherited']);
export const TagTargetKind = z.enum(['asset', 'snippet', 'phrase']);
export const VideoFormat = z.enum(['9:16', '1:1', '16:9']);
export const Platform = z.enum(['tiktok', 'instagram', 'youtube_shorts', 'pinterest']);
export const AudioMode = z.enum(['original', 'trending']);
export const SlotAudio = z.enum(['keep', 'duck', 'mute']);
export const CaptionStyle = z.enum(['clean', 'bold', 'singalong']);
export const TextMode = z.enum(['none', 'captions', 'title']);
export const PostStatus = z.enum(['draft', 'awaiting_approval', 'approved', 'scheduled', 'published', 'linked']);
export const UsageKind = z.enum(['indexed', 'composed', 'rendered', 'published', 'linked', 'hidden', 'deleted']);
/**
 * What an audio library row is: music under a whole cut, identified music, one effect on one clip, or a
 * voiceover (a recording, or a clean spoken line judged out of a clip).
 */
export const AudioKind = z.enum(['bed', 'track', 'sfx', 'voice']);
/** Which rung of the naming ladder named it; `user` is never overwritten by a machine. */
export const AudioIdentifiedBy = z.enum(['filename', 'fingerprint', 'ai', 'user']);
export const GrantTargetKind = z.enum(['asset', 'snippet', 'phrase', 'audio_bed', 'collection']);
export const GrantScope = z.enum(['view', 'use']);
/**
 * The facet set every brand starts from, whatever it sells; the facet editor makes it their own. Variant
 * is a flavour to a food brand, a shade to a cosmetics one, a size or a style to anyone else.
 */
export const DEFAULT_FACETS = [
    { key: 'product', label: 'Product' },
    { key: 'sku', label: 'SKU' },
    { key: 'variant', label: 'Variant' },
    { key: 'colour', label: 'Colour' },
    { key: 'pack', label: 'Pack' },
    { key: 'persona', label: 'Persona' },
    { key: 'subject', label: 'Subject' },
    { key: 'format', label: 'Format' },
];
/** Per-platform caption limits used by the caption builder. */
export const PLATFORM_RULES = {
    tiktok: { captionMax: 2200, hashtagsMax: 5, label: 'TikTok' },
    instagram: { captionMax: 2200, hashtagsMax: 8, label: 'Instagram' },
    youtube_shorts: { captionMax: 100, hashtagsMax: 3, label: 'YouTube Shorts' },
    pinterest: { captionMax: 500, hashtagsMax: 5, label: 'Pinterest' },
};
/** Dimensions per output format at 1080 wide or tall. */
export const FORMAT_DIMENSIONS = {
    '9:16': { width: 1080, height: 1920 },
    '1:1': { width: 1080, height: 1080 },
    '16:9': { width: 1920, height: 1080 },
};
//# sourceMappingURL=media.js.map